import type { AppLaunchIntent } from '../navigation/appLaunchIntent'

interface NativeStartupRouteBridge {
  takeLaunchSeed(): string | null
  notePainted?(noteId: string): void
}

interface NativeStartupRouteContainer {
  MiniManyStartupRoute?: NativeStartupRouteBridge
}

declare global {
  interface Window {
    MiniManyStartupRoute?: NativeStartupRouteBridge
  }
}

// Match the native/app route contract without constraining the UUID version nibble.
const CANONICAL_NOTE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Lift only the seeded native presentation cover; durable ACK remains independent. */
export function revealPaintedNativeNote(noteId: string): () => void {
  let frame = requestAnimationFrame(() => {
    frame = requestAnimationFrame(() => {
      try { window.MiniManyStartupRoute?.notePainted?.(noteId) } catch { /* Older hosts use ACK. */ }
    })
  })
  return () => cancelAnimationFrame(frame)
}

/**
 * Consumes the one-shot, content-free route hint installed by the Android host before
 * the application script evaluates. Durable payload adoption and acknowledgement stay
 * in the native inbox and run after React becomes interactive.
 */
export function readNativeStartupLaunchIntent(
  container: NativeStartupRouteContainer = window,
): AppLaunchIntent | null {
  let rawSeed: string | null
  try {
    rawSeed = container.MiniManyStartupRoute?.takeLaunchSeed() ?? null
  } catch {
    return null
  }
  if (!rawSeed) return null

  try {
    const seed: unknown = JSON.parse(rawSeed)
    if (!seed || typeof seed !== 'object') return null
    const route = seed as Record<string, unknown>
    if (route.kind === 'capture' && (route.source === 'widget' || route.source === 'share')) {
      return {
        kind: 'capture',
        source: route.source,
        sharedBody: '',
        shareId: null,
        shareError: false,
      }
    }
    if (route.kind === 'search' && route.source === 'widget') {
      return { kind: 'search', source: 'widget', focus: true }
    }
    if (
      route.kind === 'open-note'
      && (route.source === 'launch' || route.source === 'shortcut' || route.source === 'widget')
      && typeof route.noteId === 'string'
      && CANONICAL_NOTE_ID.test(route.noteId)
    ) {
      return { kind: 'open-note', source: route.source, noteId: route.noteId.toLowerCase() }
    }
  } catch {
    return null
  }
  return null
}
